// ADR-0034 §5 — Block JSON 스펙 (WP 호환)
// Gutenberg block shape + 우리 확장:
//   - `_id` (uuidv7 stable across revisions, ADR §2 P5)
//   - `rawHtml` (WP delimiter innerHTML 보존, codex v2 verdict #7)
//   - `metadata.reusableRef` (Phase 2 = flat expand)

import { z } from "zod";

/** WP-compatible name pattern: `core/<x>` | `roottale/<x>` */
export const BLOCK_NAME_PATTERN = /^(core|roottale)\/[a-z0-9-]+$/;

const blockMetadataSchema = z
  .object({
    bindings: z
      .record(
        z.object({
          source: z.string(),
          args: z.record(z.unknown()).optional(),
        }),
      )
      .optional(),
    name: z.string().optional(),
    reusableRef: z.string().optional(),
  })
  .strict()
  .optional();

/**
 * Block schema (recursive — innerBlocks).
 * `_id` 는 internal-only (export 시 strip, ADR §5).
 */
export const blockSchema: z.ZodType<Block> = z.lazy(() =>
  z
    .object({
      _id: z.string().uuid(),
      name: z.string().regex(BLOCK_NAME_PATTERN),
      attributes: z.record(z.unknown()),
      innerBlocks: z.array(blockSchema),
      rawHtml: z.string().optional(),
      metadata: blockMetadataSchema,
    })
    .strict(),
);

export interface Block {
  /** uuidv7. Internal-only — never exported as WP delimiter attribute. */
  _id: string;
  /** `core/<x>` or `roottale/<x>` */
  name: string;
  attributes: Record<string, unknown>;
  innerBlocks: Block[];
  /** WP delimiter innerHTML (preserved for round-trip — codex v2 verdict #7). */
  rawHtml?: string;
  metadata?: {
    bindings?: Record<string, { source: string; args?: Record<string, unknown> }>;
    name?: string;
    reusableRef?: string;
  };
}

export function isValidBlock(value: unknown): value is Block {
  return blockSchema.safeParse(value).success;
}

export function parseBlock(value: unknown): Block {
  return blockSchema.parse(value);
}

/**
 * Phase 1 core block names (10) — ADR-0034 §5.
 * Phase 2+ 에 `core/embed`, `core/gallery`, `core/cover` 등 추가.
 */
export const PHASE_1_CORE_BLOCKS = [
  "core/paragraph",
  "core/heading",
  "core/image",
  "core/list",
  "core/quote",
  "core/code",
  "core/columns",
  "core/group",
  "core/separator",
  "core/spacer",
] as const;
export type Phase1CoreBlockName = (typeof PHASE_1_CORE_BLOCKS)[number];
